import { useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowUpRight, Check, RotateCcw, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { services } from '../data/content';
import { L } from '../i18n/LocaleProvider';

const fields = [
  { name: 'name', label: 'Full name', placeholder: 'Alex Morgan', required: true, autoComplete: 'name' },
  { name: 'email', label: 'Work email', placeholder: 'alex@company.com', required: true, type: 'email', autoComplete: 'email' },
  { name: 'company', label: 'Company', placeholder: 'Your company', autoComplete: 'organization' },
];

export const ContactForm = () => {
  const [params] = useSearchParams();
  const preset = services.some(s => s.id === params.get('service')) ? params.get('service') : '';
  const [interest, setInterest] = useState(preset);
  const [values, setValues] = useState({ name: '', email: '', company: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  const update = e => {
    setValues(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
    if (submitError) setSubmitError('');
  };

  const submit = async e => {
    e.preventDefault();
    setSubmitError('');
    const next = {};
    if (values.name.trim().length < 2) next.name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) next.email = 'Please enter a valid work email.';
    if (!values.message.trim()) next.message = 'Tell us a little about what you have in mind.';
    setErrors(next);
    if (Object.keys(next).length) {
      formRef.current?.querySelector(`[name="${Object.keys(next)[0]}"]`)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (typeof window !== 'undefined' && window.location.port === '3000'
          ? 'http://localhost:5000'
          : '');

      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          company: values.company.trim(),
          interest: interest || '',
          message: values.message.trim(),
        }),
      });

      if (!response.ok) {
        let errMessage = 'Failed to send message. Please try again.';
        try {
          const errData = await response.json();
          if (errData?.detail) {
            errMessage = typeof errData.detail === 'string' ? errData.detail : JSON.stringify(errData.detail);
          }
        } catch {
          // fallback
        }
        throw new Error(errMessage);
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Unable to submit your message. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setValues({ name: '', email: '', company: '', message: '' });
    setInterest('');
    setSubmitted(false);
    setErrors({});
    setSubmitError('');
  };

  return (
    <L>
      <div className="contact-form-card glass" data-testid="contact-form-card">
        {submitted ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="form-success" role="status" aria-live="polite" data-testid="contact-success">
            <span className="success-check"><Check size={32} /></span>
            <span className="eyebrow" data-testid="contact-success-eyebrow">HERE’S TO WHAT’S NEXT</span>
            <h2 data-testid="contact-success-heading">Thanks, <span translate="no">{values.name.trim().split(' ')[0]}</span>.</h2>
            <p data-testid="contact-success-message">Your message is complete.<br />Great things start with a conversation.</p>
            <div className="success-preview-note" data-testid="contact-success-note">Your message has been received. Our team will contact you shortly.</div>
            <Button className="action-button button-secondary" onClick={reset} data-testid="contact-reset-button">Write another message <RotateCcw size={16} /></Button>
          </motion.div>
        ) : (
          <form ref={formRef} onSubmit={submit} noValidate data-testid="contact-form">
            <div className="form-header">
              <h2 data-testid="contact-form-heading">Tell us what’s on your mind.</h2>
              <ArrowUpRight size={24} aria-hidden="true" />
            </div>
            <div className="form-grid">
              {fields.map(field => (
                <div className={`form-field ${field.name === 'company' ? 'form-full' : ''}`} key={field.name}>
                  <label htmlFor={`contact-${field.name}`} data-testid={`contact-${field.name}-label`}>
                    {field.label} {field.required && <span>*</span>}{!field.required && <small>(optional)</small>}
                  </label>
                  <Input
                    id={`contact-${field.name}`}
                    name={field.name}
                    value={values[field.name]}
                    onChange={update}
                    placeholder={field.placeholder}
                    autoComplete={field.autoComplete}
                    type={field.type || 'text'}
                    required={field.required}
                    maxLength={field.name === 'email' ? 254 : 150}
                    className={`form-input ${errors[field.name] ? 'input-error' : ''}`}
                    data-testid={`contact-${field.name}`}
                    aria-invalid={!!errors[field.name]}
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                    disabled={submitting}
                  />
                  {errors[field.name] && <p id={`${field.name}-error`} className="field-error" data-testid={`contact-${field.name}-error`}>{errors[field.name]}</p>}
                </div>
              ))}
              <div className="form-field form-full">
                <label htmlFor="contact-interest" data-testid="contact-interest-label">Interested in <small>(optional)</small></label>
                <Select value={interest} onValueChange={setInterest} name="interest" disabled={submitting}>
                  <SelectTrigger id="contact-interest" className="form-input" data-testid="contact-interest">
                    <SelectValue placeholder="Where can we help?" />
                  </SelectTrigger>
                  <SelectContent className="interest-options" data-testid="contact-interest-options" data-lenis-prevent>
                    {services.map(s => <SelectItem value={s.id} key={s.id} data-testid={`interest-option-${s.id}`}>{s.name}</SelectItem>)}
                    <SelectItem value="not-sure" data-testid="interest-option-not-sure">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field form-full">
                <label htmlFor="contact-message" data-testid="contact-message-label">Message <span>*</span></label>
                <Textarea
                  id="contact-message"
                  name="message"
                  placeholder="A little about your project, goals, or the challenge ahead…"
                  value={values.message}
                  onChange={update}
                  required
                  maxLength={5000}
                  className={`form-input form-textarea ${errors.message ? 'input-error' : ''}`}
                  data-testid="contact-message"
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                  disabled={submitting}
                />
                {errors.message && <p id="message-error" className="field-error" data-testid="contact-message-error">{errors.message}</p>}
              </div>
            </div>

            {submitError && (
              <div className="contact-form-error" role="alert" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ff6b6b', fontSize: '13px', margin: '14px 0 6px' }} data-testid="contact-form-error">
                <AlertCircle size={16} />
                <span>{submitError}</span>
              </div>
            )}

            <div className="form-submit-row">
              <Button type="submit" className="action-button button-primary" data-testid="contact-submit" disabled={submitting}>
                {submitting ? (
                  <>Sending… <Loader2 size={17} className="animate-spin" /></>
                ) : (
                  <>Send Message <ArrowRight size={17} /></>
                )}
              </Button>
              <span data-testid="contact-required-note">* Required fields</span>
            </div>
            <p className="form-privacy-note" data-testid="contact-privacy-note">By submitting, you agree to our <Link to="/privacy-policy">Privacy Policy</Link>.</p>
            <p className="form-demo-note" data-testid="contact-demo-note">Inquiries are typically answered within 24 hours.</p>
          </form>
        )}
      </div>
    </L>
  );
};